export const tenBhumis = {
  id: "ten-bhumis",
  title: "The Ten Bhumis",
  subtitle: "Daśabhūmi — The Ten Grounds of the Bodhisattva",
  description:
    "The Ten Bhūmis represent the ten stages of a bodhisattva's path to full buddhahood in Mahāyāna Buddhism. Each bhūmi marks a profound transformation in wisdom, compassion, and spiritual capacity. As described in the Daśabhūmika Sūtra and the Avatamsaka Sūtra, these stages unfold progressively, each building upon the realizations of the previous one.",
  crossReferences: [
    "six-paramitas",
    "ten-paramitas",
    "four-immeasurables",
    "five-paths",
    "three-bodies",
  ],
  items: [
    {
      number: 1,
      sanskrit: "Pramuditā",
      name: "Joyous",
      meaning: "The Ground of Joy",
      paramita: "Dāna (Generosity)",
      description:
        "The bodhisattva first directly perceives emptiness (śūnyatā) and enters the path of seeing. Overwhelmed with joy at this realization and the certainty of future buddhahood, they naturally practice extraordinary generosity. They abandon the three fetters: belief in a self, doubt, and attachment to rites and rituals.",
      realization:
        "Direct perception of emptiness; entry into the Noble Sangha",
      qualities: [
        "Able to visit 100 buddha-fields in a single instant",
        "Can emanate 100 bodies simultaneously",
        "Possesses unshakeable faith and joy",
        "Abandons gross afflictive obscurations",
        "Generates immeasurable aspiration for all beings",
      ],
      abandonments: [
        "Belief in a personal self (satkāyadṛṣṭi)",
        "Doubt about the path (vicikitsā)",
        "Attachment to rules and rituals (śīlavrataparāmarśa)",
      ],
      crossReferences: ["five-paths", "three-fetters"],
    },
    {
      number: 2,
      sanskrit: "Vimalā",
      name: "Stainless",
      meaning: "The Ground of Purity",
      paramita: "Śīla (Moral Discipline)",
      description:
        "The bodhisattva's ethical conduct becomes spontaneously pure, free from even the subtlest transgressions. Their morality is effortless and immaculate, arising naturally from wisdom rather than mere rule-following. They perfect the ten virtuous actions and are free from all stains of immorality.",
      realization: "Perfection of natural, effortless ethical purity",
      qualities: [
        "Able to visit 1,000 buddha-fields",
        "Ethical conduct is spontaneous and flawless",
        "Radiates moral purity that inspires others",
        "Dreams become purified and meaningful",
        "Speech becomes naturally truthful and beneficial",
      ],
      abandonments: [
        "Subtle tendencies toward misconduct",
        "Inclination toward lesser vehicles",
        "Residual carelessness in action",
      ],
      crossReferences: ["ten-virtuous-actions", "three-types-of-morality"],
    },
    {
      number: 3,
      sanskrit: "Prabhākarī",
      name: "Luminous",
      meaning: "The Ground of Radiance",
      paramita: "Kṣānti (Patience)",
      description:
        "Through deep meditative absorption, the bodhisattva's mind becomes luminously clear. They master the four dhyānas and attain extraordinary patience — patience with beings, patience with suffering, and patience with the profound dharma. The light of their understanding illuminates the nature of all phenomena.",
      realization:
        "Mastery of meditative absorptions; illuminating wisdom arises",
      qualities: [
        "Able to visit 100,000 buddha-fields",
        "Attains the four dhyānas and formless absorptions",
        "Patience becomes unshakeable in all circumstances",
        "Memory of past lives becomes clear",
        "Gains extraordinary powers of concentration",
      ],
      abandonments: [
        "Anger and irritation in all forms",
        "Attachment to meditative experiences",
        "Impatience with the slow progress of beings",
      ],
      crossReferences: [
        "four-dhyanas",
        "four-formless-absorptions",
        "three-types-of-patience",
      ],
    },
    {
      number: 4,
      sanskrit: "Arciṣmatī",
      name: "Radiant",
      meaning: "The Ground of Blazing Brilliance",
      paramita: "Vīrya (Diligence)",
      description:
        "The bodhisattva's spiritual energy blazes like fire, burning away the subtle obscurations. They master the thirty-seven factors of enlightenment (bodhipakṣya dharma) with tireless diligence. Their practice is characterized by heroic effort that never wavers, regardless of obstacles.",
      realization:
        "Perfection of the wings of enlightenment; blazing wisdom-fire",
      qualities: [
        "Able to visit billions of buddha-fields",
        "Masters the 37 factors of enlightenment",
        "Effort becomes tireless and spontaneous",
        "Burns away innate afflictions through wisdom-fire",
        "Generates boundless enthusiasm for practice",
      ],
      abandonments: [
        "Laziness and procrastination in all forms",
        "Attachment to worldly pleasures",
        "Subtle forms of self-grasping related to the dharma",
      ],
      crossReferences: [
        "thirty-seven-factors",
        "four-right-efforts",
        "five-powers",
      ],
    },
    {
      number: 5,
      sanskrit: "Sudurjayā",
      name: "Difficult to Conquer",
      meaning: "The Ground of Invincibility",
      paramita: "Dhyāna (Meditative Concentration)",
      description:
        "The bodhisattva masters the relationship between conventional and ultimate truth, harmonizing them perfectly. This stage is 'difficult to conquer' because the bodhisattva must overcome the extreme subtleties of distraction. They achieve mastery over meditative absorption while remaining engaged with the world.",
      realization:
        "Harmonization of the two truths; mastery of meditation-in-action",
      qualities: [
        "Able to visit inconceivable buddha-fields",
        "Harmonizes conventional and ultimate truth",
        "Masters all forms of meditative concentration",
        "Can benefit beings while in deep absorption",
        "Understands the Four Noble Truths at the deepest level",
      ],
      abandonments: [
        "Subtle attraction to nirvana as an escape",
        "Inability to balance meditation and activity",
        "Residual cognitive obscurations regarding the two truths",
      ],
      crossReferences: [
        "two-truths",
        "four-noble-truths",
        "nine-stages-of-shamatha",
      ],
    },
    {
      number: 6,
      sanskrit: "Abhimukhī",
      name: "Approaching",
      meaning: "The Ground of Manifest Presence",
      paramita: "Prajñā (Wisdom)",
      description:
        "The bodhisattva approaches the direct experience of the dharmakāya. Wisdom into dependent origination (pratītyasamutpāda) becomes profound and complete. They fully understand the twelve links of dependent origination in both forward and reverse order. This bhūmi is often considered the culmination of the first six perfections.",
      realization:
        "Profound insight into dependent origination; approaching the dharmakāya",
      qualities: [
        "Wisdom into emptiness and dependent origination matures fully",
        "Masters the twelve links of dependent origination",
        "Could enter nirvana but chooses to remain for beings",
        "Perfects wisdom that sees the nature of all phenomena",
        "Attains the 'patience of the unborn' (anutpattikadharmakṣānti)",
      ],
      abandonments: [
        "Subtle grasping at characteristics of phenomena",
        "Residual conceptual elaboration about emptiness",
        "Any remaining attraction to personal liberation",
      ],
      crossReferences: [
        "twelve-links",
        "dependent-origination",
        "three-doors-of-liberation",
      ],
    },
    {
      number: 7,
      sanskrit: "Dūraṅgamā",
      name: "Far-Going",
      meaning: "The Ground of Going Far Beyond",
      paramita: "Upāya (Skillful Means)",
      description:
        "The bodhisattva goes far beyond the attainment of the arhats and pratyekabuddhas. They master skillful means (upāya), adapting their teaching and activity to perfectly suit each being. Moment-to-moment, they can enter and rise from cessation while maintaining their bodhisattva vow. This marks the beginning of the 'pure grounds.'",
      realization:
        "Transcendence of the lesser vehicles; mastery of skillful means",
      qualities: [
        "Surpasses the realization of arhats and pratyekabuddhas",
        "Masters skillful means for teaching all beings",
        "Can enter and arise from cessation at will",
        "Begins the three 'pure grounds' (7th-10th)",
        "Effortless activity arises spontaneously",
      ],
      abandonments: [
        "Subtle attachment to effortful practice",
        "Residual limitations in skillful means",
        "Any trace of the hinayāna motivation",
      ],
      crossReferences: ["three-vehicles", "skillful-means", "six-paramitas"],
    },
    {
      number: 8,
      sanskrit: "Acalā",
      name: "Immovable",
      meaning: "The Ground of Unshakeable Stability",
      paramita: "Praṇidhāna (Aspiration/Vow)",
      description:
        "The bodhisattva becomes immovable, unshakeable by any circumstance. They receive an irreversible prophecy of buddhahood. All conceptual effort ceases — their activity becomes completely spontaneous and effortless. They attain the ten masteries (vaśitā) over life, mind, necessities, karma, birth, creative will, aspiration, miraculous powers, wisdom, and dharma.",
      realization:
        "Irreversibility; spontaneous, effortless activity; ten masteries",
      qualities: [
        "Receives irreversible prophecy of buddhahood (vyākaraṇa)",
        "Attains the ten masteries (daśa-vaśitā)",
        "All activity becomes spontaneous and non-conceptual",
        "Cannot be moved by any circumstance or māra",
        "Manifests in countless forms simultaneously",
      ],
      abandonments: [
        "All effort and striving in practice",
        "Any possibility of falling back",
        "Conceptual engagement with the path",
      ],
      crossReferences: ["ten-masteries", "irreversibility", "three-bodies"],
    },
    {
      number: 9,
      sanskrit: "Sādhumatī",
      name: "Excellent Intelligence",
      meaning: "The Ground of Perfect Wisdom",
      paramita: "Bala (Spiritual Power)",
      description:
        "The bodhisattva attains the four specific perfect knowledges (pratisaṃvid): knowledge of dharma, meaning, language, and eloquence. They can teach the dharma in all languages, to all beings, perfectly suited to each one's capacity. They possess complete analytical knowledge and can resolve all doctrinal questions.",
      realization:
        "Four perfect knowledges; ability to teach all beings flawlessly",
      qualities: [
        "Attains the four perfect knowledges (pratisaṃvid)",
        "Can teach in all languages simultaneously",
        "Perfectly understands the capacities of all beings",
        "Resolves all apparent contradictions in dharma",
        "Activities benefit countless beings without effort",
      ],
      abandonments: [
        "Any limitation in understanding dharma",
        "Inability to express realization perfectly",
        "Residual cognitive obscurations",
      ],
      crossReferences: [
        "four-perfect-knowledges",
        "ten-powers-of-buddha",
        "four-fearlessnesses",
      ],
    },
    {
      number: 10,
      sanskrit: "Dharmameghā",
      name: "Cloud of Dharma",
      meaning: "The Ground of the Dharma Cloud",
      paramita: "Jñāna (Primordial Wisdom)",
      description:
        "The bodhisattva sits on a great lotus throne, surrounded by countless bodhisattvas, receiving empowerment from all buddhas. Like a great cloud that rains dharma upon all beings, their activity is all-pervading and inexhaustible. At the culmination of this bhūmi, vajra-like samādhi arises and all remaining obscurations are destroyed, resulting in the attainment of perfect, complete buddhahood (samyaksambodhi).",
      realization:
        "All-pervading dharma activity; at culmination — full buddhahood",
      qualities: [
        "Receives empowerment (abhiṣeka) from all buddhas",
        "Dharma activity pervades space like a cloud raining nectar",
        "Possesses all qualities of buddhahood in nascent form",
        "Sits upon a great lotus throne in a vast assembly",
        "Vajra-like samādhi destroys final obscurations",
      ],
      abandonments: [
        "The very subtlest cognitive obscurations (jñeyāvaraṇa)",
        "All remaining traces of ignorance",
        "Any separation from complete omniscience",
      ],
      crossReferences: [
        "vajra-samadhi",
        "three-bodies",
        "buddhahood",
        "ten-powers-of-buddha",
      ],
    },
  ],
};
